/**
 * Registros planos para la vista «por registro» (Registros y detalle de lista).
 *
 * Único lugar que mapea las fuentes legacy (`getmylots` agrupado,
 * `getsharedlist?format=grouped`, `getmycadparcels`) a la card de
 * `GET /getmyrecords` (contrato: `argenlotapi/docs/frontend/FRONTEND_RECORDS.md`).
 * Con la fuente `api` (`recordsSource.js`) las cards llegan ya en esta forma y
 * pasan por `normalizeApiRecord`; `toRecordCard` y la UI no cambian.
 *
 * Reglas del contrato:
 * - Todas las cards tienen las mismas claves (`RECORD_FIELDS`), null si no aplica.
 * - UF: clave `user_lot_id`; coordenada del lote (`coord_source: 'lot'`).
 * - Lote: `property_type: 'terreno'`; precio y operación del contenido;
 *   `stock_status` null. Es registro si tiene contenido o es ítem directo de
 *   lista. Lote con contenido + UF → 1 card lote + N UF, mismo `group_key`.
 * - Marcador (`marker`, solo si se pide): favorito sin UF ni contenido.
 * - CAD: `operation` null; `group_key` `cad_project:<project_id>`.
 * - Sin precio (null o ≤ 0) → `price` y `currency` null.
 * - `boundary` `[{lat,lng}]`: silueta de fallback de la foto (`LotShapeThumbnail`).
 *
 * Extras del Front (no son del contrato): `source` ({ lot, unit, parcel }, filas
 * originales para las acciones), `has_lot_content`, `cad_name` y `records_source`
 * (`api` | `legacy`: de dónde salieron los datos de la card; va en el DOM como
 * `data-records-source` para diagnosticar).
 *
 * Foto: portada marcada → primera foto → silueta (`boundary`). Si la foto no
 * carga, la card pasa a la silueta.
 *
 * @see docs/maps/registros-record-cards.md
 */

import { formatListingPrice, OPERATION_LABEL } from './listingCoreConstants';
import { mapOperationFromApi, mapStockStatusFromApi } from './userLotFormUtils';
import { getOfficialUnitFromUserLot, getOfficialUnitNumber } from './lotFunctionalUnits';
import { formatSurfaceDisplay, parseSurfaceM2 } from './surfaceDisplay';
import { getDisplayUnits } from './userLotUnits';
import { buildListingLocationLabel } from './brandedListingItems';
import { hasSharedLotUserContentDisplayData, LOT_CONTENT_OPERATION_LABEL } from './lotUserContentFormUtils';
import { normalizeBuildingType, UF_BUILDING_TYPES } from './ufAttributeFormUtils';
import { isCadRegistrosRow } from '../../Showroom/utils/cadRegistros';
import { getLotContentSummary } from './lotContentSummary';

export const RECORD_KIND = Object.freeze({ LOT: 'lote', UNIT: 'uf', CAD: 'cad', MARKER: 'marker' });

/** Claves de una card de `getmyrecords` (`commercial_summary` solo con include). */
export const RECORD_FIELDS = Object.freeze([
  'record_key', 'record_kind', 'lot_id', 'user_lot_id', 'cad_parcel_id', 'project',
  'official_unit', 'title', 'unit_label', 'property_type', 'operation',
  'surface_m2', 'surface_source', 'bedrooms', 'bathrooms', 'parking_lots',
  'stock_status', 'price', 'currency', 'currency_confirmed', 'cad_kind', 'use', 'block',
  'cover_image', 'boundary', 'boundary_source', 'location', 'lat', 'lng', 'coord_source',
  'group_key', 'lot_name', 'lot_unit_count', 'lists', 'created_at', 'updated_at',
  'commercial_summary',
]);

const PRIVATE_ATTRIBUTE_KEYS = [
  'bedrooms',
  'full_bathrooms',
  'parking_lots',
  'total_surface',
  'rooms',
  'unit_floor',
  'covered_surface',
  'open_surface',
  'land_surface',
  'building_type',
];

const PROPERTY_TYPE_LABEL = {
  ...Object.fromEntries(UF_BUILDING_TYPES.map(({ id, label }) => [id, label])),
  terreno: 'Terreno',
};

const text = (value) => {
  if (value == null) return null;
  const out = String(value).trim();
  return out ? out : null;
};

const firstText = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const out = text(values[i]);
    if (out) return out;
  }
  return null;
};

const finiteNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Entero ≥ 0 (como `_int` de la API: 0 se conserva; la card no lo muestra). */
const countValue = (value) => {
  const n = finiteNumber(value);
  return n == null || n < 0 ? null : Math.trunc(n);
};

const surfaceM2 = (value) => {
  const n = parseSurfaceM2(value);
  return Number.isNaN(n) || n <= 0 ? null : n;
};

const pickCommercialSummary = (summary) => (
  summary && typeof summary === 'object' ? summary : null
);

/** Sin precio (null o ≤ 0) → precio y moneda null. Moneda en mayúsculas. */
const priceAndCurrency = (price, currency) => {
  const amount = finiteNumber(price);
  if (amount == null || amount <= 0) return { price: null, currency: null };
  return { price: amount, currency: text(currency)?.toUpperCase() || null };
};

/** Silueta `[{lat,lng}]` (formato de `LotShapeThumbnail`); vacía → null. */
const toBoundary = (value) => {
  if (!Array.isArray(value)) return null;
  const points = value
    .map((p) => {
      if (Array.isArray(p) && p.length >= 2) return { lng: Number(p[0]), lat: Number(p[1]) };
      return p && typeof p === 'object' ? { lat: Number(p.lat), lng: Number(p.lng) } : null;
    })
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  return points.length >= 3 ? points : null;
};

/** `lot_content_counts` (ronda 2) → { images, videos, attachments }; sin el campo → null. */
const toLotContentCounts = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const n = (value) => {
    const v = Number(value);
    return Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
  };
  return { images: n(raw.images_count), videos: n(raw.videos_count), attachments: n(raw.attachments_count) };
};

/**
 * `shape_local` de CAD (ronda 2): puntos `[[x, y]]` en 0..1 (Y hacia abajo) y
 * `aspect_ratio` (ancho / alto). Inválido o con menos de 3 puntos → null.
 */
const toShapeLocal = (raw) => {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.points)) return null;
  const points = raw.points
    .map((p) => (Array.isArray(p) && p.length >= 2 ? [Number(p[0]), Number(p[1])] : null))
    .filter((p) => p && p.every((v) => Number.isFinite(v) && v >= 0 && v <= 1));
  if (points.length < 3) return null;
  const ratio = Number(raw.aspect_ratio);
  return { points, aspect_ratio: Number.isFinite(ratio) && ratio > 0 ? ratio : 1 };
};

/** Completa las claves del contrato que falten (null) sin pisar las que vienen. */
export function withRecordDefaults(record) {
  const out = { ...(record || {}) };
  RECORD_FIELDS.forEach((key) => {
    if (out[key] === undefined) out[key] = null;
  });
  return out;
}

/**
 * Copia en la raíz de la UF los campos que `getmylots` solo manda dentro de
 * `private_attributes` (dormitorios, baños, cochera, superficies…). La raíz
 * gana si ya trae el dato. No muta la fila original.
 * @param {object|null|undefined} unit
 * @returns {object|null}
 */
export function flattenUnitPrivateAttributes(unit) {
  if (!unit || typeof unit !== 'object') return unit ?? null;
  const attrs = unit.private_attributes;
  if (!attrs || typeof attrs !== 'object') return unit;
  const out = { ...unit };
  PRIVATE_ATTRIBUTE_KEYS.forEach((key) => {
    if ((out[key] == null || out[key] === '') && attrs[key] != null && attrs[key] !== '') {
      out[key] = attrs[key];
    }
  });
  return out;
}

/**
 * Ubicación como `location_payload` de la API: calle (+ número), barrio,
 * localidad, municipio, provincia; `label` = calle · barrio · localidad (o
 * municipio), sin repetidos. Sin datos → null.
 */
const buildLocation = (lot, unit = null) => {
  const street = (source) => {
    const name = firstText(source?.street_name, source?.street);
    if (!name) return null;
    const number = text(source?.street_number);
    return number ? `${name} ${number}` : name;
  };
  const loc = {
    street: street(unit) || street(lot),
    neighborhood: firstText(unit?.neighborhood_name, lot?.neighborhood_name, lot?.neighborhood),
    locality: firstText(unit?.locality_name, lot?.locality_name),
    municipality: firstText(unit?.municipality_name, lot?.municipality_name),
    province: firstText(unit?.province_name, lot?.province_name),
  };
  const parts = [];
  [loc.street, loc.neighborhood, loc.locality || loc.municipality].forEach((value) => {
    if (value && !parts.some((p) => p.toLowerCase() === value.toLowerCase())) parts.push(value);
  });
  if (!parts.length) {
    // getmylots no siempre trae localidad: la etiqueta del share (con departamento) completa.
    const fallback = buildListingLocationLabel(lot || {}, unit);
    if (fallback && fallback !== '—') parts.push(fallback);
  }
  if (!parts.length && !Object.values(loc).some(Boolean)) return null;
  return { ...loc, label: parts.join(' · ') || null };
};

const toCoverImage = (cover) => {
  if (!cover) return null;
  if (typeof cover === 'string') {
    return text(cover) ? { id: null, url: cover, created_at: null, is_cover: true } : null;
  }
  const url = firstText(cover.url, cover.public_url, cover.image_url);
  if (!url) return null;
  return {
    id: cover.id ?? null,
    url,
    created_at: cover.created_at ?? null,
    is_cover: cover.is_cover !== false,
  };
};

const firstImageCover = (images) => {
  const list = Array.isArray(images) ? images : [];
  return toCoverImage(list.find((img) => img?.is_cover) || list[0] || null);
};

const lotUnitCount = (lot) => {
  const units = getDisplayUnits(lot);
  return units.length || countValue(lot?.unit_count) || 0;
};

/** Superficie del lote (`lot_surface` en la API; getmylots también trae la urbana). */
const lotSurface = (lot) => surfaceM2(lot?.lot_surface ?? lot?.surface)
  ?? surfaceM2(lot?.lot_urbansurface ?? lot?.urbansurface)
  ?? surfaceM2(lot?.filter_attributes?.surface ?? lot?.filter_attributes?.urbansurface);

/** Contenido de lote: propio (`lot_content`, include=lot_content_summary) o compartido (`lot_user_content`). */
const resolveLotContent = (lot) => {
  if (lot?.lot_content && typeof lot.lot_content === 'object') {
    const c = lot.lot_content;
    return {
      title: text(c.title),
      operation: mapOperationFromApi(c),
      price: c.price,
      currency: c.currency,
      cover: toCoverImage(c.cover_url),
    };
  }
  if (hasSharedLotUserContentDisplayData(lot?.lot_user_content)) {
    const c = lot.lot_user_content;
    return {
      title: text(c.title),
      operation: mapOperationFromApi(c),
      price: c.price,
      currency: c.currency,
      cover: toCoverImage(c.cover_image) || firstImageCover(c.images),
    };
  }
  return null;
};

const lotHasContent = (lot) => (
  lot?.has_lot_content === true
  || lot?.item_type === 'lot_content'
  || hasSharedLotUserContentDisplayData(lot?.lot_user_content)
);

const lotBoundary = (lot, unit = null) => toBoundary(unit?.boundary) || toBoundary(lot?.boundary);

const baseLotFields = (lot, unit = null) => {
  const lat = finiteNumber(lot?.lat);
  const lng = finiteNumber(lot?.lng);
  const hasPoint = lat != null && lng != null;
  return {
    lot_id: text(lot?.lot_id),
    cad_parcel_id: null,
    project: null,
    cad_kind: null,
    use: null,
    block: null,
    lot_name: text(lot?.lot_name),
    lot_unit_count: lotUnitCount(lot),
    group_key: lot?.lot_id ? `lot:${lot.lot_id}` : null,
    lat: hasPoint ? lat : null,
    lng: hasPoint ? lng : null,
    coord_source: hasPoint ? 'lot' : null,
    location: buildLocation(lot, unit),
    currency_confirmed: null,
  };
};

/** Superficie de UF como `uf_surface`: total → cubierta + descubierta → terreno → lote. */
const unitSurface = (unit, lot) => {
  const total = surfaceM2(unit.total_surface);
  if (total != null) return { surface_m2: total, surface_source: 'total_surface' };
  const covered = surfaceM2(unit.covered_surface) || 0;
  const open = surfaceM2(unit.open_surface) || 0;
  if (covered || open) return { surface_m2: covered + open, surface_source: 'covered_open' };
  const land = surfaceM2(unit.land_surface);
  if (land != null) return { surface_m2: land, surface_source: 'land_surface' };
  const lotValue = lotSurface(lot);
  if (lotValue != null) return { surface_m2: lotValue, surface_source: 'lot_surface' };
  return { surface_m2: null, surface_source: null };
};

/** Con portada no hace falta silueta (como la API sin `include=shape`); igual se deja si viene. */
const shapeFields = (boundary, source) => (
  boundary ? { boundary, boundary_source: source } : { boundary: null, boundary_source: null }
);

const unitRecord = (lot, rawUnit) => {
  const unit = flattenUnitPrivateAttributes(rawUnit);
  const userLotId = text(unit?.id ?? unit?.user_lot_id);
  if (!userLotId) return null;
  const officialNumber = getOfficialUnitNumber(getOfficialUnitFromUserLot(unit))
    || text(unit.official_unit?.unit_number);
  return withRecordDefaults({
    records_source: 'legacy',
    record_key: `${RECORD_KIND.UNIT}:${userLotId}`,
    record_kind: RECORD_KIND.UNIT,
    ...baseLotFields(lot, unit),
    user_lot_id: userLotId,
    official_unit: officialNumber ? { unit_number: String(officialNumber) } : null,
    title: firstText(unit.title, unit.unit_label, lot?.lot_name),
    unit_label: text(unit.unit_label),
    property_type: normalizeBuildingType(unit.building_type) || text(unit.building_type),
    operation: mapOperationFromApi(unit),
    ...unitSurface(unit, lot),
    bedrooms: countValue(unit.bedrooms),
    bathrooms: countValue(unit.full_bathrooms ?? unit.bathrooms),
    parking_lots: countValue(unit.parking_lots),
    stock_status: mapStockStatusFromApi(unit),
    ...priceAndCurrency(unit.price, unit.currency),
    cover_image: toCoverImage(unit.cover_image) || firstImageCover(unit.images),
    ...shapeFields(lotBoundary(lot, rawUnit), 'lot'),
    lists: Array.isArray(unit.lists) ? unit.lists : [],
    commercial_summary: pickCommercialSummary(unit.commercial_summary),
    created_at: unit.created_at || null,
    updated_at: unit.updated_at || null,
    source: { lot, unit: rawUnit, parcel: null },
  });
};

const lotRecord = (lot, kind = RECORD_KIND.LOT) => {
  const content = kind === RECORD_KIND.LOT ? resolveLotContent(lot) : null;
  const surface = lotSurface(lot);
  return withRecordDefaults({
    records_source: 'legacy',
    record_key: `${kind}:${lot.lot_id}`,
    record_kind: kind,
    ...baseLotFields(lot),
    user_lot_id: null,
    official_unit: null,
    title: firstText(content?.title, lot?.lot_name),
    unit_label: null,
    property_type: 'terreno',
    operation: content?.operation || null,
    surface_m2: surface,
    surface_source: surface != null ? 'lot_surface' : null,
    bedrooms: null,
    bathrooms: null,
    parking_lots: null,
    stock_status: null,
    ...priceAndCurrency(content?.price, content?.currency),
    cover_image: content?.cover || toCoverImage(lot?.cover_image),
    ...shapeFields(lotBoundary(lot), 'lot'),
    // Contrato: `lists` solo en UF. El lote directo de lista sigue en `source.lot`.
    lists: null,
    commercial_summary: pickCommercialSummary(lot?.commercial_summary),
    created_at: lot?.created_at || null,
    updated_at: lot?.latest_activity_at || lot?.updated_at || null,
    has_lot_content: kind === RECORD_KIND.LOT && lotHasContent(lot),
    source: { lot, unit: null, parcel: null },
  });
};

const isMarkerOnlyLot = (lot, units) => (
  !units.length && !lotHasContent(lot) && lot?.item_type === 'marker'
);

/**
 * Registros de un lote agrupado (`getmylots` / `getsharedlist?format=grouped`).
 * @param {object} lot
 * @param {{ includeMarkers?: boolean }} [options] - `kind=marker`: favoritos sin UF ni contenido.
 * @returns {object[]}
 */
export function lotRowToRecords(lot, { includeMarkers = false } = {}) {
  if (!lot?.lot_id) return [];
  const units = getDisplayUnits(lot);
  if (isMarkerOnlyLot(lot, units)) {
    return includeMarkers ? [lotRecord(lot, RECORD_KIND.MARKER)] : [];
  }
  const records = [];
  // Contenido de lote primero (como la fila del acordeón); lote directo sin UF también.
  if (lotHasContent(lot) || !units.length) records.push(lotRecord(lot));
  units.forEach((unit) => {
    const record = unitRecord(lot, unit);
    if (record) records.push(record);
  });
  return records;
}

/**
 * Registro de una parcela CAD (`getmycadparcels`, ya normalizada para Registros).
 * @param {object} row
 * @returns {object|null}
 */
export function cadParcelToRecord(row) {
  const id = text(row?.id ?? row?.cad_parcel_id);
  if (!id) return null;
  const lat = finiteNumber(row.lat);
  const lng = finiteNumber(row.lng);
  const hasPoint = lat != null && lng != null;
  const projectId = text(row.project_id);
  const project = projectId || row.project_slug || row.project_name
    ? { id: projectId, slug: text(row.project_slug), name: text(row.project_name) }
    : null;
  const area = surfaceM2(row.area_m2);
  const cadKind = text(row.cad_kind ?? row.kind) || 'lot';
  const boundary = toBoundary(row.boundary);
  return withRecordDefaults({
    records_source: 'legacy',
    record_key: `${RECORD_KIND.CAD}:${id}`,
    record_kind: RECORD_KIND.CAD,
    lot_id: null,
    user_lot_id: null,
    cad_parcel_id: id,
    project,
    official_unit: null,
    title: firstText(row.title, row.name),
    unit_label: null,
    property_type: cadKind === 'lot' ? 'terreno' : cadKind,
    operation: null,
    surface_m2: area,
    surface_source: area != null ? 'area_m2' : null,
    bedrooms: null,
    bathrooms: null,
    parking_lots: null,
    stock_status: mapStockStatusFromApi(row),
    ...priceAndCurrency(row.price, row.currency),
    currency_confirmed: null,
    cad_kind: cadKind,
    use: text(row.use),
    block: text(row.block),
    cover_image: toCoverImage(row.cover_image),
    ...shapeFields(boundary, 'cad_parcel'),
    // Lote de referencia del showroom: getmycadparcels no lo trae (la API sí).
    location: null,
    lat: hasPoint ? lat : null,
    lng: hasPoint ? lng : null,
    coord_source: hasPoint ? 'cad_parcel' : null,
    group_key: projectId ? `cad_project:${projectId}` : null,
    lot_name: null,
    lot_unit_count: null,
    lists: null,
    commercial_summary: pickCommercialSummary(row.commercial_summary),
    created_at: row.created_at || null,
    updated_at: row.latest_activity_at || row.updated_at || null,
    cad_name: text(row.name),
    source: { lot: null, unit: null, parcel: row },
  });
}

/**
 * Filas mezcladas de Registros / detalle de lista → registros planos, en el
 * mismo orden (lote con contenido: lote primero, después sus UF).
 * @param {object[]} rows
 * @param {{ includeMarkers?: boolean }} [options]
 * @returns {object[]}
 */
export function toRecords(rows, options = {}) {
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (isCadRegistrosRow(row)) {
      const record = cadParcelToRecord(row);
      if (record) out.push(record);
      return;
    }
    out.push(...lotRowToRecords(row, options));
  });
  return out;
}

/**
 * Card de `getmyrecords` (o `getsharedlist?format=records`) → registro del Front:
 * mismas claves, moneda en mayúsculas, silueta validada. `has_lot_content` no
 * viene en el contrato: se infiere (título distinto del nombre del lote, precio,
 * operación o portada) hasta que la API lo mande.
 * @param {object} card
 * @param {{ source?: object|null, hasLotContent?: boolean|null }} [extra] - filas legacy si las hay
 * @returns {object|null}
 */
export function normalizeApiRecord(card, { source = null, hasLotContent = null } = {}) {
  if (!card || typeof card !== 'object' || !card.record_key) return null;
  const record = withRecordDefaults(card);
  const money = priceAndCurrency(record.price, record.currency);
  const kind = record.record_kind;
  // Ronda 2: `has_lot_content` viene en la card lote. Sin el campo se infiere.
  const apiHasContent = typeof card.has_lot_content === 'boolean' ? card.has_lot_content : null;
  const inferredContent = kind === RECORD_KIND.LOT && Boolean(
    (record.title && record.title !== record.lot_name)
    || money.price != null
    || record.operation
    || record.cover_image,
  );
  return {
    ...record,
    ...money,
    group_key: record.group_key || null,
    boundary: toBoundary(record.boundary),
    boundary_source: toBoundary(record.boundary) ? record.boundary_source : null,
    // Portada: la marcada; si no, la primera foto. La silueta solo sin fotos.
    cover_image: toCoverImage(record.cover_image) || firstImageCover(card.images),
    commercial_summary: pickCommercialSummary(record.commercial_summary),
    records_source: 'api',
    has_lot_content: kind === RECORD_KIND.LOT
      ? (apiHasContent ?? hasLotContent ?? inferredContent) === true
      : false,
    lot_content_counts: kind === RECORD_KIND.LOT ? toLotContentCounts(card.lot_content_counts) : null,
    // Ronda 2: `lists` del lote ([] si no está en ninguna); sin el campo, null.
    lists: Array.isArray(card.lists) ? card.lists : null,
    cad_name: text(card.cad_name) || text(card.parcel_name) || null,
    shape_local: kind === RECORD_KIND.CAD ? toShapeLocal(card.shape_local) : null,
    source: source || {
      lot: kind === RECORD_KIND.CAD ? null : {
        lot_id: record.lot_id,
        lot_name: record.lot_name,
        lat: record.lat,
        lng: record.lng,
        boundary: record.boundary,
        has_lot_content: kind === RECORD_KIND.LOT ? (apiHasContent ?? inferredContent) : undefined,
        units: [],
      },
      unit: kind === RECORD_KIND.UNIT ? { id: record.user_lot_id, unit_label: record.unit_label, title: record.title } : null,
      parcel: kind === RECORD_KIND.CAD ? { id: record.cad_parcel_id, project_id: record.project?.id, project_slug: record.project?.slug, project_name: record.project?.name, lat: record.lat, lng: record.lng } : null,
    },
  };
}

/**
 * Fuente `api` con la carga legacy todavía activa (mapa, filtros de parcela,
 * vista agrupada y acciones usan las filas de `getmylots`): para cada registro
 * legacy toma la card de la API si está (portada de UF, silueta, ubicación),
 * conservando `source` y `has_lot_content` legacy. Orden y universo = legacy
 * (ya filtrado); lo que la API no trajo queda con la versión legacy.
 * @param {object[]} legacyRecords - `toRecords(rows)`
 * @param {object[]} apiCards - `data` de getmyrecords
 * @returns {object[]}
 */
const recordMatchKey = (kind, id) => (kind && id ? `${String(kind).toLowerCase()}:${String(id).trim().toLowerCase()}` : null);

/** Claves de cruce: `record_key` y, por si difiere el formato, `kind:id` (UF por user_lot_id). */
const apiMatchKeys = (record) => [
  record?.record_key ? String(record.record_key).trim().toLowerCase() : null,
  recordMatchKey(record?.record_kind, record?.user_lot_id || record?.cad_parcel_id || record?.lot_id),
].filter(Boolean);

export function mergeApiRecords(legacyRecords, apiCards) {
  const byKey = new Map();
  (Array.isArray(apiCards) ? apiCards : []).forEach((card) => {
    apiMatchKeys(card).forEach((key) => { if (!byKey.has(key)) byKey.set(key, card); });
  });
  return (Array.isArray(legacyRecords) ? legacyRecords : []).map((legacy) => {
    const card = apiMatchKeys(legacy).map((key) => byKey.get(key)).find(Boolean);
    if (!card) return legacy;
    const record = normalizeApiRecord(card, { source: legacy.source, hasLotContent: legacy.has_lot_content });
    return {
      ...record,
      // Contrato: `lists` null en lote / CAD y `commercial_summary` solo con include.
      // La API trae la portada de la UF; si no trajo, la legacy (getsharedlist sí la manda).
      cover_image: record.cover_image || legacy.cover_image,
      lists: record.lists ?? legacy.lists,
      // Contadores de medios: los de la API (ronda 2) o el resumen legacy.
      lot_content_counts: record.lot_content_counts,
      commercial_summary: record.commercial_summary ?? legacy.commercial_summary,
      cad_name: record.cad_name ?? legacy.cad_name ?? null,
    };
  });
}

/**
 * Totales en la forma de la API (`total`, `totals_by_kind`) a partir de registros.
 * @param {object[]} records
 * @returns {{ total: number, totals_by_kind: { lote: number, uf: number, cad: number, marker: number } }}
 */
export function summarizeRecords(records) {
  const totals = { lote: 0, uf: 0, cad: 0, marker: 0 };
  (Array.isArray(records) ? records : []).forEach((r) => {
    if (totals[r?.record_kind] != null) totals[r.record_kind] += 1;
  });
  return { total: totals.lote + totals.uf + totals.cad + totals.marker, totals_by_kind: totals };
}

/**
 * Agrupa registros por `group_key` conservando el orden de aparición. Sin
 * `group_key` (CAD sin proyecto), cada registro es su propio grupo.
 * @param {object[]} records
 * @returns {Array<{ group_key: string, records: object[] }>}
 */
export function groupRecordsByGroupKey(records) {
  const groups = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    const key = record?.group_key || record?.record_key;
    if (!key) return;
    if (!groups.has(key)) groups.set(key, { group_key: key, records: [] });
    groups.get(key).records.push(record);
  });
  return [...groups.values()];
}

export const getPropertyTypeLabel = (type) => {
  const key = text(type);
  if (!key) return null;
  return PROPERTY_TYPE_LABEL[key.toLowerCase()] || key.charAt(0).toUpperCase() + key.slice(1);
};

const operationLabel = (record) => {
  if (!record.operation) return null;
  return OPERATION_LABEL[record.operation] || LOT_CONTENT_OPERATION_LABEL[record.operation] || null;
};

const formatSurfaceValue = (m2) => {
  const display = formatSurfaceDisplay(m2);
  return display ? `${display.value} ${display.unit}` : null;
};

const RECORD_TITLE_FALLBACK = {
  [RECORD_KIND.UNIT]: 'Unidad sin título',
  [RECORD_KIND.MARKER]: 'Marcador',
  [RECORD_KIND.LOT]: 'Contenido del lote',
  [RECORD_KIND.CAD]: 'Lote',
};

const resolveTitles = (record) => {
  if (record.record_kind === RECORD_KIND.UNIT) {
    const label = record.unit_label;
    const title = record.title;
    if (label && title && label.localeCompare(title, 'es', { sensitivity: 'accent' }) !== 0) {
      return { title: label, subtitle: title };
    }
    return { title: label || title || RECORD_TITLE_FALLBACK.uf, subtitle: null };
  }
  if (record.record_kind === RECORD_KIND.CAD) {
    return { title: record.title || record.cad_name || RECORD_TITLE_FALLBACK.cad, subtitle: null };
  }
  // El contrato cae al nombre del lote: si el título es ese, no es un título propio.
  if (record.title && record.title !== record.lot_name) return { title: record.title, subtitle: null };
  // Lote directo / marcador sin contenido: la calle es lo más legible; si no, el catastral.
  return {
    title: record.has_lot_content
      ? RECORD_TITLE_FALLBACK.lote
      : (record.location?.street || `Nº catastral ${record.lot_name || record.lot_id}`),
    subtitle: null,
  };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Metas de la card de registro, en una línea de valores autoexplicativos
 * («45 m² · 3 dorm. · 2 baños · UF 12»): sin fila de labels, la card es más baja.
 * El estado no va acá: es el badge fijo sobre la foto (`stock_status`).
 * Sin precio, el tipo de propiedad va primero (con precio va en el caption).
 */
const buildRecordMetas = (record, { typeInCaption }) => {
  const metas = [];
  const typeLabel = getPropertyTypeLabel(record.property_type);
  if (!typeInCaption && typeLabel) {
    metas.push({ key: 'type', label: 'Tipo', value: typeLabel });
  }
  const surface = record.surface_m2 != null ? formatSurfaceValue(record.surface_m2) : null;
  if (surface) metas.push({ key: 'surface', label: 'Superficie', value: surface });
  if (record.bedrooms > 0) {
    metas.push({ key: 'bedrooms', label: 'Dormitorios', value: `${record.bedrooms} dorm.` });
  }
  if (record.bathrooms > 0) {
    metas.push({ key: 'bathrooms', label: 'Baños', value: plural(record.bathrooms, 'baño', 'baños') });
  }
  if (record.parking_lots > 0) {
    metas.push({ key: 'parking_lots', label: 'Cocheras', value: plural(record.parking_lots, 'cochera', 'cocheras') });
  }
  if (record.official_unit?.unit_number) {
    metas.push({ key: 'official_unit', label: 'UF catastral', value: `UF ${record.official_unit.unit_number}` });
  }
  if (record.record_kind === RECORD_KIND.CAD && record.block) {
    metas.push({ key: 'block', label: 'Manzana', value: `Mz. ${record.block}` });
  }
  if ((record.record_kind === RECORD_KIND.LOT || record.record_kind === RECORD_KIND.MARKER) && record.lot_unit_count > 0) {
    metas.push({ key: 'units', label: 'Unidades', value: plural(record.lot_unit_count, 'unidad', 'unidades') });
  }
  return metas;
};

/**
 * Registro → item de `BrandedListingCard` (misma card que el share).
 * El item expone `commercial_summary` sin `units`, así los filtros y órdenes
 * comerciales (`Leads/recordCommercialFilters`) lo evalúan por card.
 * @param {object} record
 * @returns {object}
 */
/**
 * Resumen del tooltip de contenido: con `lot_content_counts` (ronda 2) sale de la
 * card; si no, de la fila legacy (`lot_content` / `lot_user_content`).
 */
const resolveLotContentSummary = (record) => {
  if (record.lot_content_counts) {
    const priceLabel = record.price != null ? formatListingPrice(record.price, record.currency) : null;
    const listing = [priceLabel, operationLabel(record)].filter(Boolean).join(' · ');
    return {
      title: record.title && record.title !== record.lot_name ? record.title : '',
      listing,
      ...record.lot_content_counts,
      coverUrl: record.cover_image?.url || null,
    };
  }
  return getLotContentSummary(record.source?.lot);
};

export function toRecordCard(record) {
  const priceLabel = record.price != null && record.price > 0
    ? formatListingPrice(record.price, record.currency)
    : null;
  const typeLabel = getPropertyTypeLabel(record.property_type);
  const opLabel = operationLabel(record);
  const hero = priceLabel
    ? {
      kind: 'price',
      amount: priceLabel,
      caption: [typeLabel, opLabel].filter(Boolean).join(' · ') || 'Precio',
      source: record.record_kind,
    }
    : null;
  const { title, subtitle } = resolveTitles(record);
  const isCad = record.record_kind === RECORD_KIND.CAD;
  const itemType = isCad ? 'cad' : (record.record_kind === RECORD_KIND.UNIT ? 'unit' : 'lot');
  const cadName = record.cad_name || text(record.source?.parcel?.name);
  return {
    key: record.record_key,
    record,
    itemType,
    lot: record.source?.lot || null,
    unit: record.source?.unit || null,
    parcel: record.source?.parcel || null,
    lotId: record.lot_id,
    userLotId: record.user_lot_id,
    title,
    subtitle,
    hero,
    metas: buildRecordMetas(record, { typeInCaption: Boolean(hero) }),
    coverImage: record.cover_image?.url || null,
    // Silueta de fallback de la foto (`getmyrecords` con include=shape, o la del lote).
    boundary: record.boundary || null,
    locationLabel: record.location?.label || record.project?.name || (isCad ? 'Lista' : '—'),
    cadastralLabel: isCad ? (cadName || record.title || '—') : (record.lot_name || '—'),
    copyIdLabel: isCad ? 'Copiar identificador del lote' : 'Copiar número catastral',
    hasLotContent: record.record_kind === RECORD_KIND.LOT && record.has_lot_content === true,
    // Tooltip del ícono de contenido (mismo resumen que la vista agrupada).
    lotContentSummary: record.record_kind === RECORD_KIND.LOT && record.has_lot_content === true
      ? resolveLotContentSummary(record)
      : null,
    // CAD sin portada ni silueta georreferenciada: plano local (ronda 2) o ícono.
    shapeLocal: isCad ? record.shape_local || null : null,
    stock_status: record.stock_status,
    commercial_summary: record.commercial_summary,
    commercialIsCad: isCad,
  };
}

/**
 * Atajo: filas actuales → items de card. Con `apiCards` (fuente `api`), cada
 * registro legacy toma la card de `getmyrecords` (`mergeApiRecords`).
 * @param {object[]} rows
 * @returns {object[]}
 */
export const buildRecordCardItems = (rows, { apiCards = null } = {}) => {
  const records = toRecords(rows);
  return (Array.isArray(apiCards) ? mergeApiRecords(records, apiCards) : records).map(toRecordCard);
};

/**
 * Totales del toolbar a partir de las cards visibles tras filtros: lotes =
 * lotes catastrales distintos + lotes CAD; unidades = cards de UF; contenido =
 * cards de lote con contenido.
 * @param {object[]} items
 * @returns {{ lots: number, units: number, lotContent: number }}
 */
export function countRecordCardItems(items) {
  const lotIds = new Set();
  let cad = 0;
  let units = 0;
  let lotContent = 0;
  (Array.isArray(items) ? items : []).forEach((item) => {
    const kind = item?.record?.record_kind;
    if (kind === RECORD_KIND.CAD) {
      cad += 1;
      return;
    }
    if (item?.lotId) lotIds.add(String(item.lotId));
    if (kind === RECORD_KIND.UNIT) units += 1;
    if (kind === RECORD_KIND.LOT && item.hasLotContent) lotContent += 1;
  });
  return { lots: lotIds.size + cad, units, lotContent };
}

/**
 * Ids de lote catastral presentes en las cards (para pintar el mapa con el
 * mismo conjunto que se ve en la lista).
 * @param {object[]} items
 * @returns {Set<string>}
 */
export const getRecordCardLotIds = (items) => new Set(
  (Array.isArray(items) ? items : [])
    .map((item) => (item?.lotId != null ? String(item.lotId) : null))
    .filter(Boolean),
);

/** USD antes que ARS; otras monedas después; sin moneda al final. */
const CURRENCY_SORT_RANK = { USD: 0, ARS: 1 };
const currencyRank = (currency) => {
  const key = text(currency)?.toUpperCase();
  if (!key) return 3;
  return CURRENCY_SORT_RANK[key] ?? 2;
};

const recordPrice = (item) => {
  const price = item?.record?.price;
  return price != null && Number(price) > 0 ? Number(price) : null;
};

/**
 * Comparador de precio por registro: primero moneda (USD, ARS, otras), después
 * monto (asc / desc) y los registros sin precio al final. Empate → 0.
 * @param {object} a item de card
 * @param {object} b item de card
 * @param {1|-1} dir
 */
export function compareRecordCardPrice(a, b, dir = 1) {
  const pa = recordPrice(a);
  const pb = recordPrice(b);
  if (pa == null && pb == null) return 0;
  if (pa == null) return 1;
  if (pb == null) return -1;
  const rank = currencyRank(a.record.currency) - currencyRank(b.record.currency);
  if (rank !== 0) return rank;
  return dir * (pa - pb);
}

const recordSurface = (item) => {
  const value = item?.record?.surface_m2;
  return value != null && Number(value) > 0 ? Number(value) : null;
};

/**
 * Orden de la vista por registro con las opciones de `listSort` (precio,
 * superficie, consultas). «Nombre» y lo que no reconoce conservan el orden de
 * entrada (lotes ya ordenados; dentro del lote, contenido y después sus UF).
 * Estable: en empate queda el orden previo.
 * @param {object[]} items
 * @param {string} sortBy
 * @param {{ getInquiryTotal?: (item: object) => number }} [options]
 * @returns {object[]}
 */
export function sortRecordCardItems(items, sortBy, { getInquiryTotal } = {}) {
  const list = (Array.isArray(items) ? items : []).map((item, index) => ({ item, index }));
  const sort = String(sortBy || '');
  const desc = sort.endsWith('-desc') ? -1 : 1;
  let compare = null;
  if (sort.startsWith('price-')) {
    compare = (a, b) => compareRecordCardPrice(a, b, desc);
  } else if (sort.startsWith('area-')) {
    compare = (a, b) => {
      const sa = recordSurface(a);
      const sb = recordSurface(b);
      if (sa == null && sb == null) return 0;
      if (sa == null) return 1;
      if (sb == null) return -1;
      return desc * (sa - sb);
    };
  } else if (sort.startsWith('inquiries-') && getInquiryTotal) {
    compare = (a, b) => desc * ((Number(getInquiryTotal(a)) || 0) - (Number(getInquiryTotal(b)) || 0));
  }
  if (!compare) return list.map(({ item }) => item);
  list.sort((x, y) => compare(x.item, y.item) || (x.index - y.index));
  return list.map(({ item }) => item);
}

/**
 * Preparado para F2 (chips de precio en el mapa, uno por registro): agrupa los
 * registros con coordenada por `group_key`. Las UF de un lote comparten la
 * coordenada del lote, así que cada grupo es un punto que se abre en abanico;
 * `fanIndex` / `fanCount` dicen el lugar de cada registro dentro del abanico.
 * Los registros sin coordenada quedan afuera.
 * @param {object[]} records
 * @returns {Array<{ point_key: string, group_key: string, lat: number, lng: number, records: Array<object & { fanIndex: number, fanCount: number }> }>}
 */
export function groupRecordsForMap(records) {
  // Clave = group_key + punto: las UF de un lote comparten coordenada y se abren
  // en abanico; las parcelas CAD de un proyecto comparten group_key
  // (`cad_project:<id>`) pero cada una tiene su centroide, así que no se juntan.
  const keyed = (Array.isArray(records) ? records : [])
    .filter((r) => r?.lat != null && r?.lng != null)
    .map((r) => ({
      ...r,
      group_key: `${r.group_key || r.record_key}@${Number(r.lat).toFixed(6)},${Number(r.lng).toFixed(6)}`,
      __groupKey: r.group_key,
    }));
  return groupRecordsByGroupKey(keyed).map((group) => ({
    // Único por punto (clave de React / feature del mapa).
    point_key: group.group_key,
    group_key: group.records[0].__groupKey || group.records[0].record_key,
    lat: group.records[0].lat,
    lng: group.records[0].lng,
    records: group.records.map(({ __groupKey, ...record }, fanIndex) => ({
      ...record,
      group_key: __groupKey,
      fanIndex,
      fanCount: group.records.length,
    })),
  }));
}

/**
 * Precio compacto para chips de mapa (F2): «USD 260k», «ARS 1,2M». Sin precio → null.
 * @param {number|null} price
 * @param {string|null} currency
 * @returns {string|null}
 */
export function formatCompactRecordPrice(price, currency) {
  const n = Number(price);
  if (price == null || price === '' || !Number.isFinite(n) || n <= 0) return null;
  const code = text(currency)?.toUpperCase() || 'USD';
  const fmt = (value, suffix) => `${code} ${value.toLocaleString('es-AR', { maximumFractionDigits: value < 10 ? 1 : 0 })}${suffix}`;
  if (n >= 1_000_000) return fmt(n / 1_000_000, 'M');
  if (n >= 1_000) return fmt(n / 1_000, 'k');
  return `${code} ${Math.round(n).toLocaleString('es-AR')}`;
}
